// build: progression — the chord-loop builder. A row of slots (config.slots,
// default 4); tap a slot, then tap a degree chip 1–7 to put that chord in it
// (the chord previews as you pick). config.pin = { 0: 1 } pins a slot to a
// degree — pinned slots render locked and aren't editable. ▶/■ loops the
// progression via playPhraseLoop(chordPhrase(degrees), { times: 2 });
// describeProgression narrates the trip under the slots.
//
// Soft checks (moment.checks) run against { degrees } once every slot is
// filled — warm nudges, never verdicts: "save keeper" lights when they all
// pass, and "keep it anyway" is ALWAYS offered when one doesn't. Saving calls
// onKeeper(keeperId, keeperName, { phrase: chordPhrase(degrees), degrees })
// and unlocks Continue. Scales down gracefully to one- and two-slot builds.
//
// Unit 6: config.chordMode: 'minor' renders/plays everything in minor mode
// (MINOR_CHORD_PERSONA words, chordPhrase(..., { mode: 'minor' }), keeper
// phrase in minor voicings). config.raisedFive adds a 5♯ chip — the borrowed
// doorbell — stored in degrees as the theory lib's '5+' token.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  chordPhrase,
  CHORD_PERSONA,
  MINOR_CHORD_PERSONA,
  describeProgression,
  runChecks,
} from "../../../lib/homeKeyTheory";
import {
  PATCHES,
  playPhrase,
  playPhraseLoop,
  resumeAudio,
} from "../../../lib/homeKeyAudio";

const ALL_DEGREES = [1, 2, 3, 4, 5, 6, 7];

// The raised-five slot choice stores the theory lib's string token.
const RAISED = "5+";

const chipLabel = (degree) => (degree === RAISED ? "5♯" : degree);

export default function BuildMoment({ moment, onReadyChange, onKeeper }) {
  const config = moment.config || {};
  const slotCount = config.slots || 4;
  const pin = config.pin || {};
  const isMinor = config.chordMode === "minor";
  const phraseOpts = isMinor ? { mode: "minor" } : undefined;
  const personaOf = (degree) => {
    if (degree === RAISED) {
      return MINOR_CHORD_PERSONA[RAISED] || "the borrowed doorbell";
    }
    return isMinor ? MINOR_CHORD_PERSONA[degree] : CHORD_PERSONA[degree];
  };
  const chipDegrees = config.raisedFive
    ? ALL_DEGREES.reduce((list, d) => {
        list.push(d);
        if (d === 5) list.push(RAISED); // the borrowed doorbell sits by 5
        return list;
      }, [])
    : ALL_DEGREES;

  const [slots, setSlots] = useState(() =>
    Array.from({ length: slotCount }, (_, i) => (pin[i] != null ? pin[i] : null))
  );
  // Start editing at the first unpinned empty slot, if any.
  const [activeSlot, setActiveSlot] = useState(() => {
    for (let i = 0; i < slotCount; i += 1) {
      if (pin[i] == null) return i;
    }
    return null;
  });
  const [looping, setLooping] = useState(false);
  const [saved, setSaved] = useState(false);

  const loopRef = useRef(null);
  const timerRef = useRef(null);

  const stopLoop = useCallback(() => {
    if (loopRef.current && loopRef.current.stop) loopRef.current.stop();
    loopRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setLooping(false);
  }, []);
  useEffect(() => () => stopLoop(), [stopLoop]);

  const ready = saved;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const filled = slots.filter((d) => d != null);
  const complete = filled.length === slotCount;

  const results = useMemo(
    () => (complete ? runChecks(moment.checks || [], { degrees: slots }) : null),
    [complete, moment.checks, slots]
  );
  const allPass = results != null && results.every((r) => r.pass);

  const tapSlot = (i) => {
    if (saved || pin[i] != null) return;
    setActiveSlot(i);
  };

  const pickDegree = (degree) => {
    if (saved || activeSlot == null) return;
    resumeAudio();
    stopLoop();
    setSlots((prev) => {
      const next = [...prev];
      next[activeSlot] = degree;
      // Move the cursor along to the next empty, unpinned slot (if any).
      let cursor = activeSlot;
      for (let i = 0; i < next.length; i += 1) {
        if (next[i] == null && pin[i] == null) {
          cursor = i;
          break;
        }
      }
      setActiveSlot(cursor);
      return next;
    });
    playPhrase(
      chordPhrase([degree], degree === RAISED ? { mode: "minor" } : phraseOpts),
      PATCHES.softKeys
    );
  };

  const toggleLoop = () => {
    if (loopRef.current) {
      stopLoop();
      return;
    }
    if (!filled.length) return;
    resumeAudio();
    const handle = playPhraseLoop(chordPhrase(filled, phraseOpts), { times: 2 });
    loopRef.current = handle && handle.stop ? handle : null;
    setLooping(true);
    timerRef.current = setTimeout(stopLoop, ((handle && handle.durMs) || 4000) + 300);
  };

  const save = () => {
    if (!complete) return;
    stopLoop();
    onKeeper(moment.keeperId, moment.keeperName, {
      phrase: chordPhrase(slots, phraseOpts),
      degrees: [...slots],
    });
    setSaved(true);
  };

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "Fill the slots with chords — tap a slot, then pick its number. Loop it until it feels like yours."}
      </p>

      <div className="hk-slot-row" role="group" aria-label="progression slots">
        {slots.map((degree, i) => {
          const pinned = pin[i] != null;
          const active = activeSlot === i && !saved;
          return (
            <button
              key={i}
              type="button"
              className={`hk-slot${pinned ? " hk-slot--pinned" : ""}${
                active ? " hk-slot--active" : ""
              }`}
              aria-pressed={active}
              aria-label={
                degree != null
                  ? `slot ${i + 1}: the ${chipLabel(degree)} chord${
                      pinned ? " (pinned)" : ""
                    }`
                  : `slot ${i + 1}: empty`
              }
              onClick={() => tapSlot(i)}
              disabled={saved || pinned}
            >
              <span className="hk-slot-degree">
                {degree != null ? chipLabel(degree) : "+"}
              </span>
              <span className="hk-slot-word">
                {degree != null ? personaOf(degree) : "tap to pick"}
              </span>
            </button>
          );
        })}
      </div>

      {!saved && activeSlot != null && (
        <div
          className="hk-degree-chips"
          role="group"
          aria-label={`chord for slot ${activeSlot + 1}`}
        >
          {chipDegrees.map((degree) => (
            <button
              key={degree}
              type="button"
              className={`hk-degree-chip${
                degree === RAISED ? " hk-degree-chip--raised" : ""
              }${slots[activeSlot] === degree ? " hk-degree-chip--on" : ""}`}
              aria-pressed={slots[activeSlot] === degree}
              onClick={() => pickDegree(degree)}
            >
              {chipLabel(degree)}
            </button>
          ))}
        </div>
      )}

      {filled.length > 0 && (
        <p className="hk-prog-line" aria-live="polite">
          {isMinor ? describeProgression(filled, "minor") : describeProgression(filled)}
        </p>
      )}

      <div className="hk-choice-row">
        <button
          type="button"
          className={`hk-demo-btn${looping ? " hk-demo-btn--on" : ""}`}
          disabled={!filled.length}
          onClick={toggleLoop}
        >
          {looping ? "■ stop the loop" : "▶ loop it"}
        </button>
      </div>

      {!saved && complete && results && (
        <div className="hk-checks">
          {results.map((r) => (
            <div
              key={r.id}
              className={`hk-check-note${r.pass ? " hk-check-note--pass" : ""}`}
            >
              {r.pass ? "✓ " : "~ "}
              {r.message}
            </div>
          ))}
        </div>
      )}

      {!saved && (
        <div className="hk-choice-row">
          <button
            type="button"
            className="hk-btn hk-btn--primary"
            disabled={!complete || !allPass}
            onClick={save}
          >
            save keeper
          </button>
          {complete && !allPass && (
            <button type="button" className="hk-btn hk-btn--ghost" onClick={save}>
              actually, I like it — keep it anyway
            </button>
          )}
        </div>
      )}

      {saved && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of
          the music now.
        </p>
      )}
    </div>
  );
}
